// @flow
import React from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {colors} from '../../assets/styles/colors';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
} from '@material-ui/core';
import {ExpandMore} from '@material-ui/icons';
import {MuiThemeProvider, createTheme} from '@material-ui/core/styles';

import type {
  InputLabelProps as InpInputLabelPropsType,
  SelectProps,
} from '@material-ui/core';

type Props = {
  className?: string,
  label?: string,
  helperText?: string | React$Node,
  fullWidth?: boolean,
  pagination?: boolean,
  children: any,
  InputLabelProps?: InpInputLabelPropsType,
} & SelectProps;

const useStyles = makeStyles(theme => ({
  paginationSelect: {
    fontWeight: 400,
    fontSize: '0.875rem',
    border: `1px solid ${colors.grey300}`,
    borderRadius: 24,
    padding: '0 16px 0 16px',
    '&:hover': {
      background: colors.lightPurple,
    },
    '&:focus': {
      borderRadius: 12,
      background: 'white',
      borderColor: colors.lightPurple,
    },
  },
  paginationRoot: {
    padding: '8px 0px',
  },
  icon: {
    right: 12,
    position: 'absolute',
    userSelect: 'none',
    pointerEvents: 'none',
  },
  paper: {
    borderRadius: 12,
    marginTop: 8,
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
    background: 'white',
    '& li': {
      fontWeight: 400,
      fontSize: '0.875rem',
    },
    '& li:hover': {
      background: colors.lightPurple,
    },
    '& li.Mui-selected': {
      color: 'white',
      background: colors.purple2,
    },
    '& li.Mui-selected:hover': {
      background: colors.purple3,
    },
  },
  root: {
    overflow: 'hidden',
    borderRadius: '8px',
    border: `1.25px solid ${colors.grey300}`,
    boxSizing: 'border-box',
    backgroundColor: 'white !important',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
  },
  paddingTop: {
    paddingTop: '25px',
  },
  error: {
    color: '#f44336 !important',
  },
}));

const theme = createTheme({
  overrides: {
    MuiInput: {
      root: {marginTop: '0px !important'},
    },
    MuiInputBase: {
      root: {
        borderRadius: '8px',
        border: `1.25px solid ${colors.grey300}`,
        color: `${colors.grey800} !important`,
        '&$error': {
          border: '1.25px solid #f44336 !important',
        },
        '&$focused': {
          backgroundColor: `transparent !important `,
        },
        '&$disabled': {
          backgroundColor: `${colors.grey200} !important`,
        },
      },
    },
    MuiFormLabel: {
      root: {
        color: `${colors.grey600} !important`,
        paddingLeft: '12px',
        '&$error': {
          color: '#f44336 !important',
        },
        '&$focused': {
          paddingTop: '12px !important',
        },
      },
      filled: {
        paddingTop: '12px !important',
      },
    },
    MuiSelect: {
      root: {
        padding: '14px 24px 14px 14px',
        // display: 'flex',
        alignItems: 'center',
      },
    },
  },
  typography: {
    fontFamily: '"Poppins", "Muli", sans-serif',
  },
});

export default function CustomSelect(props: Props) {
  const {
    className,
    label,
    helperText,
    fullWidth,
    pagination,
    children,
    InputLabelProps,
    ...otherProps
  } = props;

  const classes = useStyles();

  const iconComponent = props => {
    return <ExpandMore className={props.className + ' ' + classes.icon} />;
  };

  const menuProps = {
    classes: {
      paper: classes.paper,
      list: classes.list,
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'center',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'center',
    },
    getContentAnchorEl: null,
  };

  return (
    <MuiThemeProvider theme={theme}>
      <FormControl
        fullWidth={fullWidth}
        // classes={{
        //   root: pagination ? '' : classes.root,
        // }}
        className={`${className ? className : ''}`}
      >
        {label && (
          <InputLabel className={classes.label} {...InputLabelProps}>
            {label}
          </InputLabel>
        )}
        <Select
          classes={{
            root: pagination
              ? classes.paginationRoot
              : label
              ? classes.paddingTop
              : '',
          }}
          className={`${classes.default} ${
            pagination ? classes.paginationSelect : ''
          }  `}
          MenuProps={pagination ? menuProps : undefined}
          IconComponent={pagination ? iconComponent : undefined}
          disableUnderline
          {...otherProps}
        >
          {children}
        </Select>
        {helperText && (
          <FormHelperText className={classes.error}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    </MuiThemeProvider>
  );
}

CustomSelect.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  helperText: PropTypes.any,
  fullWidth: PropTypes.bool,
  pagination: PropTypes.bool,
  children: PropTypes.any.isRequired,
};
